import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jspreadsheet from "jspreadsheet-ce";
import { toast } from "react-toastify";
import routes from "../../../Routes/routes";
import { errorMsg } from "../../../Apis/functions";
import { errorCodeTranslation, warnCodeTranslation } from "../../../Apis/ErrorCode";
import { UploadResidentWhiteList } from "../../../reduxToolkit/Building/BuildingApi";
import { setWhiteListImportFile, setWhiteListImportResult } from "../../../reduxToolkit/Building/BuildingSlice";
import {
    BUILDING_WHITE_LIST_AUTOMATIC_MODEL,
    WHITE_LIST_IMPORT_COLUMNS,
    WHITE_LIST_IMPORT_ERROR_COLUMN,
    WHITE_LIST_IMPORT_HEADER_ROWS,
    WHITE_LIST_TEMPLATE_HEADER,
    buildWhiteListImportFile,
    isWhiteListHeaderFailure,
    isWhiteListHeaderRow,
    whiteListFailureToValues,
    whiteListSheetRowToValues
} from "../Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Logic of the white list import errors view: reads the summary and the template
 * the white list modal left in the store, paints the failed rows of the sheet on
 * an editable grid and uploads the sheet again once they are corrected, the same
 * way the excel imports of the other modules do.
 *
 * @returns {Object} Localization function, import summary, grid ref and handlers.
 */
export const CreationErrorsWhiteListApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // The white list modal navigates here with the building it was opened from
    const { state } = useLocation();
    const buildingId = state?.buildingId;
    const importResult = useSelector((store) => store?.BuildingSlice?.whiteListImportResult);
    const importFile = useSelector((store) => store?.BuildingSlice?.whiteListImportFile);
    // Summary in use, the verify of the grid replaces it with the one it gets back
    const [result, setResult] = useState(importResult);
    // Sheet the failures point at, it is the template while it is not corrected.
    // The rows are kept exactly as the file that was uploaded holds them so the
    // row numbers the endpoint returns land on the same index
    const [sheetRows, setSheetRows] = useState([]);
    // Header rows the uploaded file holds. The template of the automatic tab
    // brings the titles of the columns, the sheet the verify sends does not:
    // the upload endpoint reads every row as a resident, so the titles are left
    // out of it and the row numbers of the answer start on the first resident
    const [sheetHeaderCount, setSheetHeaderCount] = useState(WHITE_LIST_IMPORT_HEADER_ROWS);
    // Titles of the columns read from the template, the grid keeps painting them
    // as its first row even once the sheet that travels has no header
    const [templateHeader, setTemplateHeader] = useState(null);
    // The grid waits for the template to be read so it is painted only once
    const [sheetLoaded, setSheetLoaded] = useState(false);
    // Rows of the grid, the header of the template and one row per failed row
    const [rows, setRows] = useState([]);
    const jRef = useRef(null);

    const returnedFailures = result?.failures || [];
    const savedRecords = result?.savedRecords || 0;

    /**
     * Goes back to the building detail, the list is the fallback when the view
     * is opened without the building that imported the white list.
     */
    const goBack = () => {
        if (!buildingId) {
            navigate(routes.building.list);
            return;
        }

        navigate(routes.building.details, { state: { id: buildingId } });
    };

    /**
     * Translates the reason of one failed row with the catalog of the toasts.
     *
     * @param {number} reason - Error code returned by the white list endpoints.
     * @returns {string} Message shown on the cell comment and the error card.
     */
    const getReasonMessage = (reason) => t(
        warnCodeTranslation[reason]
        || errorCodeTranslation[reason]
        || 'something_went_wrong_2'
    );

    /**
     * Header of the grid. The titles of the template are the ones painted, the
     * translated columns are the fallback of the white lists captured only by
     * hand. It is only a guide of the grid, it never travels on the sheet.
     *
     * @returns {Array} Rows painted before the failed ones.
     */
    const getHeaderRows = () => {
        if (templateHeader) return [templateHeader];

        return [WHITE_LIST_IMPORT_COLUMNS.map(
            (column) => t(column === 'lastName' ? 'last_name' : column)
        )];
    };

    /**
     * Values one failed row is painted with. The row of the uploaded sheet is
     * the one that travels, the payload of the failure is the fallback of the
     * rows the sheet does not hold, the ones captured on the manual tab.
     *
     * @param {Object} failure - Failed row returned by the white list endpoints.
     * @param {Array} sheet - Rows of the uploaded file, header rows included.
     * @returns {Array} Values in WHITE_LIST_IMPORT_COLUMNS order.
     */
    const getFailureRow = (failure, sheet) => {
        // The endpoints number the rows of the file they read, the header rows
        // it brought are the ones that are not a resident
        const sheetIndex = (failure?.rowNumber ?? 0) - 1;
        const sheetRow = sheetIndex >= sheetHeaderCount ? sheet[sheetIndex] : null;

        if (!sheetRow) return whiteListFailureToValues(failure);

        return whiteListSheetRowToValues(sheetRow);
    };

    // Header the grid is painted with, the failures are compared against it
    const headerRows = getHeaderRows();

    // The upload endpoint reads the titles of the columns as one more resident
    // and returns them as a failure. The header of the template is not a row to
    // be corrected: it is dropped from the grid, from the error cards and from
    // the totals
    const paintedFailures = returnedFailures
        .map((failure) => ({ failure, row: getFailureRow(failure, sheetRows) }))
        .filter(({ failure, row }) => !isWhiteListHeaderFailure(failure, headerRows)
            && !isWhiteListHeaderRow(row, headerRows));

    const failures = paintedFailures.map(({ failure }) => failure);
    const headerFailures = returnedFailures.length - failures.length;
    const totalRecords = (result?.totalRecords || 0) - headerFailures;
    const failedRecords = (result?.failedRecords ?? returnedFailures.length) - headerFailures;

    // The summary only lives while the view is open, it is lost on a reload and
    // there is nothing to correct without it
    useEffect(() => {
        if (!importResult) goBack();

        return () => {
            dispatch(setWhiteListImportResult(null));
            dispatch(setWhiteListImportFile(null));
        };
    }, []);

    // Reads the template of the automatic tab, the white lists captured only by
    // hand do not bring a file and are painted with what the failures return
    useEffect(() => {
        if (!importFile) {
            setSheetRows([]);
            // The manual tab does not upload a file, its rows are numbered from
            // the first resident of the payload
            setSheetHeaderCount(0);
            setSheetLoaded(true);
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            const workbook = XLSX.read(event.target.result, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const sheet = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            const rowsRead = sheet.map((row) => whiteListSheetRowToValues(row));
            // The file is uploaded without the titles of the columns, an older
            // one that still brings them is read the same way
            const hasHeader = isWhiteListHeaderRow(rowsRead[0], [WHITE_LIST_TEMPLATE_HEADER]);

            setSheetRows(rowsRead);
            setSheetHeaderCount(hasHeader ? WHITE_LIST_IMPORT_HEADER_ROWS : 0);
            setTemplateHeader(hasHeader ? rowsRead[0] : null);
            setSheetLoaded(true);
        };

        reader.onerror = () => {
            toast.error(t('something_went_wrong_2').toUpperCase());
            setSheetRows([]);
            setSheetLoaded(true);
        };

        reader.readAsArrayBuffer(importFile);
    }, [importFile]);

    // Every summary brings its own failed rows, the grid keeps the header of the
    // template and paints only the rows that have to be corrected, the saved ones
    // are left out so the verify does not create them twice
    useEffect(() => {
        if (!sheetLoaded) return;

        setRows([
            ...headerRows,
            ...paintedFailures.map(({ row }) => row)
        ]);
    }, [result, sheetRows, sheetLoaded]);

    // Builds the editable grid and paints the cell each reason points at
    useEffect(() => {
        if (!jRef.current || rows.length === 0) return;

        if (jRef.current.jexcel) {
            jRef.current.jexcel.destroy();
        }

        const instance = jspreadsheet(jRef.current, {
            data: rows,
            minDimensions: [WHITE_LIST_IMPORT_COLUMNS.length, rows.length],
            tableOverflow: true,
            columns: WHITE_LIST_IMPORT_COLUMNS.map(() => ({ type: 'text', width: 180 }))
        });

        const errorColor = getComputedStyle(document.documentElement).getPropertyValue('--error-code-600').trim();

        failures.forEach((failure, index) => {
            const columnIndex = WHITE_LIST_IMPORT_ERROR_COLUMN[failure?.reason];
            if (columnIndex === undefined) return;

            // The failed rows are painted one after the other below the header
            const cellName = `${String.fromCharCode(65 + columnIndex)}${headerRows.length + index + 1}`;

            if (instance.getCell(cellName)) {
                instance.setStyle(cellName, 'background-color', errorColor);
                instance.setComments(cellName, `${t('DESCRIPTION')}: ${getReasonMessage(failure?.reason)}`);
            }
        });

        jRef.current.jexcel = instance;
    }, [rows]);

    /**
     * Rebuilds the template with the corrected rows and uploads it again. The
     * rows that were saved on the previous try are not on the grid, only the
     * failed ones travel. When every row goes through the view goes back to the
     * building detail, otherwise the grid is filled with the new failures of the
     * sheet that was just sent.
     */
    const handleVerify = () => {
        const gridData = jRef.current?.jexcel?.getData() || [];
        // The first rows of the grid are the titles of the columns, they are only
        // a guide of the view: the upload endpoint reads every row of the file as
        // a resident, so the sheet travels with the corrected rows alone
        const dataRows = gridData
            .slice(headerRows.length)
            .filter((row) => row.some((cell) => `${cell ?? ''}`.trim() !== ''))
            .filter((row) => !isWhiteListHeaderRow(row, headerRows));

        if (dataRows.length === 0) {
            toast.warning(t('please_fill_all_fields'));
            return;
        }

        dispatch(UploadResidentWhiteList(
            BUILDING_WHITE_LIST_AUTOMATIC_MODEL.toRegister({
                buildingId,
                photoPickerFile: { fileSelected: buildWhiteListImportFile(dataRows) }
            })
        )).then(
            ({ payload: { data: { data, success, code } } }) => {
                if (!success) {
                    errorMsg(code);
                    return;
                }

                if (data?.failedRecords > 0) {
                    // The rows the new failures point at are the ones just sent,
                    // the sheet that travelled has no header rows
                    setSheetRows(dataRows);
                    setSheetHeaderCount(0);
                    setResult(data);
                    toast.warning(t('please_review_and_correct_the_identified_errors').toUpperCase());
                    return;
                }

                toast.success(t('has_been_created_successfully'));
                goBack();
            }
        );
    };

    return {
        t,
        jRef,
        failures,
        totalRecords,
        savedRecords,
        failedRecords,
        getReasonMessage,
        handleVerify,
        goBack,
    };
};

export default CreationErrorsWhiteListApp;
